import { useEffect, useRef, useState } from 'react'
import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source'

export type StreamingSseStatus =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'closed'

export interface UseStreamingSseOptions {
  url: string
  token?: string | null
  enabled?: boolean
  onMessage: (message: EventSourceMessage) => void
  /**
   * Minimum ms between `lastEventAt` state updates (reduces parent re-renders on bursty SSE).
   * Set to 0 to update on every message.
   */
  lastEventAtMinIntervalMs?: number
}

export interface UseStreamingSseResult {
  status: StreamingSseStatus
  /** Epoch ms of last SSE message with data, or null */
  lastEventAt: number | null
}

const DEFAULT_LAST_EVENT_AT_MIN_INTERVAL_MS = 1000

export function useStreamingSse({
  url,
  token,
  enabled = true,
  onMessage,
  lastEventAtMinIntervalMs = DEFAULT_LAST_EVENT_AT_MIN_INTERVAL_MS,
}: UseStreamingSseOptions): UseStreamingSseResult {
  const [status, setStatus] = useState<StreamingSseStatus>('idle')
  const [lastEventAt, setLastEventAt] = useState<number | null>(null)
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage
  const lastEventAtPushRef = useRef<number>(0)

  useEffect(() => {
    if (!enabled || !token || !url) {
      setStatus('idle')
      return
    }

    const controller = new AbortController()
    let cancelled = false
    lastEventAtPushRef.current = 0
    setStatus('connecting')

    void fetchEventSource(url, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        Authorization: `Bearer ${token}`,
      },
      signal: controller.signal,
      openWhenHidden: true,
      async onopen(response) {
        if (cancelled) return
        if (!response.ok) {
          setStatus('reconnecting')
          throw new Error(`Streaming connection failed: ${response.status}`)
        }
        setStatus('open')
      },
      onmessage(message) {
        if (cancelled) return
        if (!message.data) return
        const now = Date.now()
        const minIv = lastEventAtMinIntervalMs
        if (
          minIv <= 0 ||
          lastEventAtPushRef.current === 0 ||
          now - lastEventAtPushRef.current >= minIv
        ) {
          lastEventAtPushRef.current = now
          setLastEventAt(now)
        }
        onMessageRef.current(message)
      },
      onerror(err) {
        if (cancelled) return
        console.error('Streaming SSE error', err)
        setStatus('reconnecting')
        return 5000
      },
    })

    return () => {
      cancelled = true
      controller.abort()
      setStatus('closed')
    }
  }, [enabled, token, url, lastEventAtMinIntervalMs])

  return { status, lastEventAt }
}
